import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { API_CONFIG_ERROR } from './config/api';
import { AUTH_BYPASS, AUTH_CONFIG_ERROR, loginRequest } from './config/auth';
// Both shells export a DEFAULT component. That is the contract between this file
// and app/src/shells/ — a named export there breaks the build here.
//
// Imported eagerly, not lazily, and that is deliberate for an app that runs
// offline: a lazily-loaded shell is a chunk fetched on navigation, and on the
// line that fetch happens exactly when the Wi-Fi has dropped. One bundle that is
// already in the browser cannot fail to arrive.
import OperatorShell from './shells/OperatorShell';
import OfficeShell from './shells/OfficeShell';
// The pages, by their NAMED exports. Both spellings exist in those files; the
// named one is the stable identifier and a default import would resolve even if
// somebody swapped the component behind it.
import { LinePick } from './pages/operator/LinePick';
import { LineCheck } from './pages/operator/LineCheck';
import { Verify } from './pages/operator/Verify';
import { Receive } from './pages/operator/Receive';
import { Landing } from './pages/Landing';
import { SpecMaster } from './pages/office/SpecMaster';
import { SpecVersion } from './pages/office/SpecVersion';
import { Meters } from './pages/office/Meters';
import { Authority } from './pages/office/Authority';
import { Competency } from './pages/office/Competency';
import { Runs } from './pages/office/Runs';
import { GoodsIn } from './pages/office/GoodsIn';
import { GoodsInReceipt } from './pages/office/GoodsInReceipt';
import { InspectionFailures } from './pages/office/InspectionFailures';
import { Holds } from './pages/office/Holds';
import { Complaints } from './pages/office/Complaints';
import { ComplaintCase } from './pages/office/ComplaintCase';
import { ComplaintAnalysis } from './pages/office/ComplaintAnalysis';
import { Documents } from './pages/office/Documents';
import { DocumentPage } from './pages/office/DocumentPage';
import { DocumentsAudit } from './pages/office/DocumentsAudit';
import { OperatorDocuments } from './pages/operator/OperatorDocuments';

/*
 * ⚠ DEEP LINKS AND app/public/staticwebapp.config.json
 *
 * Every route below except `/` is a client-side path the static host knows
 * nothing about. `staticwebapp.config.json` is what tells Azure Static Web Apps
 * to serve index.html for them, and it is only picked up if it ships in the
 * built output — which means it MUST live in `app/public/` (Vite's publicDir),
 * not at `app/` and not in the repo root. In the wrong place it is simply not
 * copied, the build is green, the home page works, and every refresh on
 * /office/specs or every link into /op/line/3 returns a 404 in production and
 * nowhere else.
 */

/** The full-screen states: booting, signing in, and failed-to-sign-in. */
function Curtain({ title, detail }: { title: string; detail?: string }) {
  return (
    <div className="flex h-full items-center justify-center p-s4">
      <div className="max-w-md text-center">
        <p className="text-base font-semibold text-ink">{title}</p>
        {detail ? <p className="mt-s2 text-body text-ink-muted">{detail}</p> : null}
      </div>
    </div>
  );
}

/**
 * Sign-in, in one place.
 *
 * The shells assume a signed-in caller and never check again — there is no
 * anonymous view of anything here, because every record in this app is attributed
 * to the UPN on the token and a page rendered without one would be showing
 * somebody else's data or none at all.
 */
function RequireSignIn({ children }: { children: ReactNode }) {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [failure, setFailure] = useState<string | null>(null);

  // A build that cannot possibly work. Checked BEFORE anything tries to sign in,
  // because a redirect against a placeholder client id fails at Microsoft with a
  // message about the application — which sends whoever reads it looking in
  // Entra for a problem that is in the deploy.
  const configError = AUTH_CONFIG_ERROR ?? API_CONFIG_ERROR;

  useEffect(() => {
    if (configError || AUTH_BYPASS || isAuthenticated || failure) return;
    // Only when MSAL is idle. Calling loginRedirect while a redirect is already
    // being processed throws `interaction_in_progress`, and the app then sits on
    // this screen for ever having thrown away the sign-in that was working.
    if (inProgress !== InteractionStatus.None) return;

    // `state` carries the page asked for, so a link sent to a phone opens that
    // page after sign-in rather than the line picker (main.tsx puts it back).
    const { pathname, search, hash } = window.location;
    void instance.loginRedirect({ ...loginRequest, state: pathname + search + hash }).catch((err: unknown) => {
      setFailure(err instanceof Error ? err.message : String(err));
    });
  }, [instance, inProgress, isAuthenticated, failure, configError]);

  if (configError) {
    return (
      <Curtain
        title="Quality is not configured correctly."
        detail={`${configError} This is a deployment setting, not a sign-in problem — reloading will not fix it.`}
      />
    );
  }

  if (AUTH_BYPASS) return <>{children}</>;

  if (failure) {
    return (
      <Curtain
        title="Sign-in failed"
        detail={`${failure} — if this persists, sign out of other Microsoft accounts in this browser and try again.`}
      />
    );
  }

  if (!isAuthenticated) return <Curtain title="Signing in…" />;

  return <>{children}</>;
}

/**
 * SpecVersion, remounted whenever the SUBJECT in the path changes.
 *
 * A `key` cannot be written on the <Route> itself — the param is not in scope
 * there — so it goes on a one-line wrapper that can read it. See the comment at
 * the route for why the remount matters.
 */
function KeyedSpecVersion() {
  const { specVersionId } = useParams<{ specVersionId: string }>();
  return <SpecVersion key={specVersionId} />;
}

export default function App() {
  return (
    <RequireSignIn>
      {/* ⚠ BOTH SHELLS RENDER <Outlet />, SO EVERY PAGE MUST BE A CHILD ROUTE
          DECLARED HERE. A shell mapped with `path="/op/*"` and no children still
          mounts — header, sync chip, held banner and all — and its Outlet renders
          nothing. The result is a screen that looks like the app with an empty
          body, which reads as "the page is loading" rather than as a routing
          mistake. A splat cannot take children either, hence `path="/op"` with a
          nested `index`. */}
      <Routes>
        {/* ⚠ DECIDED BY AUTHORITY, not hardcoded. This used to send everybody to
            `/op` because the line is by far the larger population — true, and
            still why that is the fallback, but it stranded every office user on
            a screen they had no use for with nothing linking onward. Landing
            reads the caller's grants and sends them to the work they actually
            do. See pages/Landing.tsx; the redirect is a convenience and never a
            permission. */}
        <Route path="/" element={<Landing />} />

        <Route path="/op" element={<OperatorShell />}>
          <Route index element={<LinePick />} />
          {/* LinePick navigates here and LineCheck reads `runId` with useParams.
              Renaming the parameter moves all three files. */}
          <Route path="run/:runId" element={<LineCheck />} />
          {/* The shift leader's screen. In the OPERATOR shell on purpose: it is
              used standing at the line on a phone or a second iPad, not at a
              desk, and it inherits the 56px targets and the sync chip. */}
          <Route path="run/:runId/verify" element={<Verify />} />
          {/* The dock: the incoming inspection (IIR) of a delivery, on a tablet,
              under the receiver's own login (migration 017). In the OPERATOR shell
              for the gloved targets and the handover rule. */}
          <Route path="receive" element={<Receive />} />
          {/* The documents in force that apply to whoever is signed in (rule 37). */}
          <Route path="docs" element={<OperatorDocuments />} />
          <Route path="*" element={<Navigate to="/op" replace />} />
        </Route>

        <Route path="/office" element={<OfficeShell />}>
          {/* Land on the day's work (Goods in), not the set-up. UI/UX audit, 27 Oct 2026. */}
          <Route index element={<Navigate to="/office/goods-in" replace />} />
          <Route path="specs" element={<SpecMaster />} />
          {/* SpecMaster links to /office/specs/:specVersionId and SpecVersion
              links back to /office/specs.

              ⚠ KEYED ON THE SUBJECT SO A SUBJECT CHANGE REMOUNTS. Without it,
              React reuses the component across a param change and every piece of
              state inside it survives: the selected version id, the
              have-we-heard-from-/specs latch, and — because useResource keeps its
              last payload while a new path loads — the PREVIOUS subject's version
              list and in-force answer. That is the load-order bug the page just
              fixed, wearing a different hat: one subject's approved id deciding
              what is selected for another. No screen links subject-to-subject
              today, so it is not reachable; a key makes it unreachable by
              construction rather than by the absence of a link. */}
          <Route path="specs/:specVersionId" element={<KeyedSpecVersion />} />
          <Route path="meters" element={<Meters />} />
          <Route path="authority" element={<Authority />} />
          <Route path="competency" element={<Competency />} />
          <Route path="runs" element={<Runs />} />
          {/* The CofA register and one PO line. GoodsIn links to
              /office/goods-in/:receiptId, the detail links back, and every record
              quality_cofa_record writes points a person at the same path to decide.
              Renaming the parameter moves all three. */}
          <Route path="goods-in" element={<GoodsIn />} />
          <Route path="goods-in/:receiptId" element={<GoodsInReceipt />} />
          {/* Failed dock inspections, open until QA closes them (migration 020). */}
          <Route path="inspection-failures" element={<InspectionFailures />} />
          <Route path="holds" element={<Holds />} />
          {/* Consumer complaints (migration 042), mirrored from ActiveCampaign. */}
          <Route path="complaints" element={<Complaints />} />
          <Route path="complaints/analysis" element={<ComplaintAnalysis />} />
          <Route path="complaints/:complaintId" element={<ComplaintCase />} />
          {/* Controlled documents (rule 37). "audit" before the id, or it reads as one. */}
          <Route path="documents" element={<Documents />} />
          <Route path="documents/audit" element={<DocumentsAudit />} />
          <Route path="documents/:documentId" element={<DocumentPage />} />
          {/* An unknown office URL lands on the day's work, as the index route does (UI/UX audit,
              31 Oct 2026, UX-47): /office/receipts used to land silently on Spec master, the set-up. */}
          <Route path="*" element={<Navigate to="/office/goods-in" replace />} />
        </Route>

        {/* An unknown URL lands wherever this person belongs, by the same rule
            as `/`. `replace`, so it does not sit in history waiting for the back
            button to return to it. */}
        <Route path="*" element={<Landing />} />
      </Routes>
    </RequireSignIn>
  );
}
